import { useNavigate } from "react-router-dom";
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
} from "@mui/material";
import { ScoreRating, Tag, useLoadAdvisoryCvesQuery } from "~/operations";
import { DataTable } from "../data-table";
import { cvssDisplayVersion } from "../cvss";
import { SectionHeading } from "~/components/DetailsPage/components";
import { EmptySection } from "~/components/vulnerabilities/empty-section";
import { DeprecatedRiskFactorsCell } from "./DeprecatedRiskFactorsCell";
import { SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { formatEpssScore } from "~/lib/epss";
import {
  AdvisoryCve,
  AdvisoryCves,
} from "~/pages/reports/CveOrAdvisoryReport/hooks/useCveOrAdvisoryReport";
import { ImpactCell } from "~/components/Cells";
import { TitleCell } from "~/pages/inventory/components/Findings/FindingsTable/TitleCell";
import { numberToTenths } from "~/lib/numberToTenths";

interface AdvisoryContentDetailsProps {
  id?: string;
  advisoryId: string;
  scope: SpaceOrWorkspaceScope;
}

export function AdvisoryContentDetails({
  id,
  advisoryId,
  scope,
}: AdvisoryContentDetailsProps) {
  const { data } = useLoadAdvisoryCvesQuery({
    variables: { advisoryId, scopeMrn: scope.mrn },
    skip: !scope.mrn,
  });
  const navigate = useNavigate();

  const cves: AdvisoryCves = data?.advisory?.cves || [];
  const sortedCves = [...cves].sort((a, b) => {
    if (
      b.vulnerabilityRiskFactors.knownExploited !==
      a.vulnerabilityRiskFactors.knownExploited
    ) {
      return b.vulnerabilityRiskFactors.knownExploited ? 1 : 0;
    }
    return (b.riskValue || 0) - (a.riskValue || 0);
  });

  // TODO: these tags should be includes in the API response, in "tags" property
  const getTags = (cve: AdvisoryCve): Tag[] => {
    return [
      ...(cve.cvssScore.vector
        ? [
            {
              __typename: "Tag" as const,
              key: `CVSS v${cvssDisplayVersion(cve.cvssScore.vector)}`,
              value: numberToTenths(cve.cvssScore.value),
            },
          ]
        : []),
      ...(cve.epssScore
        ? [
            {
              __typename: "Tag" as const,
              key: "EPSS",
              value: formatEpssScore(cve.epssScore),
            },
          ]
        : []),
    ];
  };

  return (
    <Box id="content-details">
      <SectionHeading heading="Content details" />
      {cves.length > 0 ? (
        <Box id={id}>
          <DataTable
            sx={{
              borderRadius: `4px !important`,
              overflow: "hidden",
              "& tr:last-child td": {
                borderBottom: "none",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Risk</TableCell>
                <TableCell>Vulnerability</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  Risk factors
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCves.map((cve) => (
                <TableRow
                  key={cve.id}
                  onClick={() =>
                    navigate(
                      `/space/vulns/CVE/${cve.id}?${scope.params}&advisoryId=${advisoryId}`,
                    )
                  }
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: (theme) =>
                        `${alpha(theme.palette.background.light, 0.6)} !important`,
                    },
                  }}
                >
                  <ImpactCell
                    rating={cve?.rating || ScoreRating.Unscored}
                    impact={cve?.riskValue || 0}
                    isActive
                  />

                  <TitleCell
                    id={cve.id}
                    title={cve.title || ""}
                    tags={getTags(cve)}
                  />

                  <DeprecatedRiskFactorsCell
                    vulnerabilityRiskFactors={cve.vulnerabilityRiskFactors}
                  />
                </TableRow>
              ))}
            </TableBody>
          </DataTable>
        </Box>
      ) : (
        <EmptySection
          id="advisory-content-details"
          text="No vulnerabilities found"
        />
      )}
    </Box>
  );
}
